import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from "react";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const Avatar = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    className={cnTwMerge("relative flex shrink-0 rounded-full", className)}
    ref={ref}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    className={cnTwMerge("aspect-square h-full w-full", className)}
    ref={ref}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

type AvatarFallbackProps = ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Fallback
> & {
  fontClassName?: string;
  showActive?: boolean;
};

const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(
  (
    { className, children, fontClassName, showActive = false, ...props },
    ref,
  ) => {
    const char =
      typeof children === "string" && children.length > 0
        ? children.charAt(0).toUpperCase()
        : null;

    return (
      <AvatarPrimitive.Fallback
        className={cnTwMerge(
          "flex h-full w-full items-center justify-center rounded-full bg-brand-default",
          className,
        )}
        ref={ref}
        {...props}
      >
        <div className="relative flex">
          <span
            className={cnTwMerge(
              { "body-small-semibold text-text/on-brand": !fontClassName },
              fontClassName,
            )}
          >
            {char}
          </span>
          {showActive ? (
            <div
              className="absolute bottom-0 right-0 translate-x-[90%] translate-y-[40%] w-2 h-2 rounded-full bg-text/positive"
              data-testid="active-indicator"
            />
          ) : null}
        </div>
      </AvatarPrimitive.Fallback>
    );
  },
);
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };
